/** Watchtower telemetry connection card (Usage tab).
 *
 *  Watchtower records each routine run's REAL token usage and cost against the
 *  user who owns the run — so the trading backend must hold a delegated
 *  authorization from that user. This card is where the user grants it: a
 *  one-time ceremony that mints a Watchtower-scoped code in the browser
 *  (`beginWatchtowerCeremony`), which the backend exchanges and custodies, then
 *  hands the user to the hosted consent picker. The console never handles the
 *  Watchtower tokens themselves — see lib/watchtower.ts for the full rationale.
 *
 *  Status comes from `GET /watchtower/status` (the watchtower-api Lambda), read
 *  through the shared Api client so it carries a freshly-refreshed bearer. */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { queryKeys, useApi } from '../lib/queries';
import { beginWatchtowerCeremony, isSafeConsentUrl } from '../lib/watchtower';

/** GET /watchtower/status — mirrors the Lambda handler verbatim. */
export interface WatchtowerStatus {
  /** A custodied refresh chain exists for THIS caller. */
  connected: boolean;
  /** Some user has been recorded as the producer's delegating owner. */
  owner_set: boolean;
  /** …and that user is this caller. */
  is_owner: boolean;
  /** The hosted consent picker URL (also used to review/revoke consent). */
  consent_url: string;
}

export default function WatchtowerCard() {
  const { config } = useAuth();
  const api = useApi();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.watchtowerStatus,
    queryFn: () => api.get<WatchtowerStatus>('/watchtower/status'),
  });
  // The ceremony ends in a full-page navigation, so `starting` normally stays
  // true until the page unloads; it only resets if the redirect itself failed.
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // The consent URL is server-supplied and rendered as an href — only ever link
  // to it when it parses as an absolute https URL.
  const consentHref = isSafeConsentUrl(data?.consent_url) ? data!.consent_url : null;

  const connect = async () => {
    setStartError(null);
    setStarting(true);
    try {
      await beginWatchtowerCeremony(config.guardiusUrl);
    } catch (e) {
      setStarting(false);
      setStartError(e instanceof Error ? e.message : 'Could not start the Watchtower connection.');
    }
  };

  return (
    <section className="watchtower-card card" aria-label="Watchtower telemetry">
      <h3 className="section-title">Watchtower telemetry</h3>

      {isLoading && <p className="note">Checking Watchtower connection…</p>}

      {isError && (
        <p className="error" role="alert">
          Failed to load Watchtower status:{' '}
          {error instanceof Error ? error.message : 'unknown error'}
        </p>
      )}

      {!isLoading && !isError && data && (
        <>
          {data.connected ? (
            <>
              {/* `connected` means only that the backend custodies a refresh
                  chain for this user — it does NOT mean delegation consent was
                  granted (the user may have abandoned the consent picker), and
                  a chain can die later. So the copy stops short of claiming
                  end-to-end success, the consent link is persistent and
                  prominent, and re-running the ceremony is always available. */}
              <p className="note">
                Connection established — telemetry also requires your delegation consent. If runs
                aren't reporting usage, grant or re-check it here.
              </p>
              <p>
                {consentHref ? (
                  <a className="btn btn-confirm" href={consentHref} target="_blank" rel="noreferrer">
                    Review or grant consent
                  </a>
                ) : (
                  <span className="error" role="alert">
                    The consent link returned by the server is unusable — reconnect to get a new one.
                  </span>
                )}
              </p>
              <button
                type="button"
                className="btn"
                onClick={() => void connect()}
                disabled={starting}
              >
                {starting ? 'Reconnecting…' : 'Reconnect'}
              </button>
            </>
          ) : (
            <>
              <p className="note">
                Connect Watchtower to record each routine run's real token usage and cost. You
                authorize it once; the connection can be revoked at any time from the consent
                screen.
              </p>
              {data.owner_set && !data.is_owner && (
                <p className="note">
                  Another member already authorized telemetry for this org — connecting also links
                  your own runs.
                </p>
              )}
              <button
                type="button"
                className="btn btn-confirm"
                onClick={() => void connect()}
                disabled={starting}
              >
                {starting ? 'Connecting…' : 'Connect Watchtower telemetry'}
              </button>
            </>
          )}
          {startError && (
            <p className="error" role="alert">
              {startError}
            </p>
          )}
        </>
      )}
    </section>
  );
}
